import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import toast from "react-hot-toast";
import { PARENT_URL } from "../utils/branding";
import Logo from "../components/Logo";
import { Mail, KeyRound, ArrowRight, ArrowLeft } from "lucide-react";

const DISPOSABLE_DOMAINS = [
  "mailinator.com", "guerrillamail.com", "10minutemail.com", "tempmail.com",
  "temp-mail.org", "yopmail.com", "throwawaymail.com", "getnada.com",
  "trashmail.com", "sharklasers.com", "maildrop.cc", "dispostable.com",
  "mailnesia.com", "fakeinbox.com", "moakt.com", "1secmail.com"
];

export default function Register() {
  const [name, setName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  // A ?target= carried from the landing hero means the visitor already told
  // us what they want scanned — send them to the scanner with it prefilled
  // instead of dropping them on an empty dashboard to retype it.
  const [params] = useSearchParams();
  const carried = (params.get("target") || "").trim();
  const afterAuth = () => (carried ? `/scan?target=${encodeURIComponent(carried)}` : "/dashboard");

  const register = async () => {
    if (!termsAccepted) { toast.error("You must accept the No-Refund Policy to register"); return; }
    if (!name.trim() || !email.trim()) { toast.error("Fill all fields"); return; }
    const domain = email.trim().split("@")[1]?.toLowerCase();
    if (!email.includes("@") || !domain) { toast.error("Enter a valid email"); return; }
    if (DISPOSABLE_DOMAINS.some(d => domain === d || domain.endsWith("." + d))) {
      toast.error("Disposable/temporary emails are not allowed. Use Gmail, Outlook, or your company email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/auth/register", { name: name.trim(), email: email.trim() });
      toast.success("Verification code sent to your email!");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter the verification code"); return; }
    setBusy(true);
    try {
      const res = await api.post("/api/auth/verify", {
        email: email.trim(),
        code: code.trim(),
        purpose: "register"
      });
      sessionStorage.setItem("aegis_token", res.data.access_token);
      toast.success("Account verified! Welcome to Aegis.");
      setTimeout(() => nav(afterAuth()), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/api/auth/register", { name: name.trim(), email: email.trim() });
      toast.success("New code sent!");
    } catch (err) {
      toast.error("Could not resend. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: "var(--au-text-2)",
    letterSpacing: 2,
    marginBottom: 10,
    fontWeight: 600,
    textTransform: "uppercase"
  };

  return (
    <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 20, position: "relative" }}>
      {/* BACK BUTTON */}
      <a className="au-pill au-pill-glass au-pill-sm" style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }} href={PARENT_URL}>
        <ArrowLeft size={16} /> Back
      </a>

      {/* subtle accent radial */}
      <div style={{
        position: "absolute",
        top: "-200px",
        right: "-200px",
        width: 500,
        height: 500,
        background: "radial-gradient(circle, var(--au-accent-glow), transparent 70%)",
        borderRadius: "50%",
        filter: "blur(60px)",
        opacity: 0.4,
        pointerEvents: "none"
      }} />

      {/* MAIN CARD */}
      <div className="au-glass au-pad-lg" style={{ maxWidth: 440, width: "100%", position: "relative", zIndex: 5 }}>
        {/* BRAND HEADER */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ marginBottom: 10 }}>
            <Logo size="large" />
          </div>
          <div style={{
            fontSize: 10,
            letterSpacing: 4,
            color: "var(--au-muted)",
            textTransform: "uppercase"
          }}>
            {step === 1 ? "CREATE ACCOUNT" : "VERIFY EMAIL"}
          </div>
        </div>

        {step === 1 && (
          <>
            <label style={labelStyle}>Full Name</label>
            <input
              className="au-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your full name"
              autoFocus
              style={{ marginBottom: 18 }}
            />

            <label style={labelStyle}>
              <Mail size={14} /> Email Address
            </label>
            <input
              className="au-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && register()}
              placeholder="you@company.com"
              style={{ marginBottom: 20 }}
            />

            <div style={{
              margin: "16px 0",
              padding: "14px 16px",
              background: "var(--au-glass-strong)",
              border: "1px solid var(--au-border)",
              borderRadius: 12,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: "var(--au-text-2)"
            }}>
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginTop: 3, minWidth: 16, minHeight: 16, accentColor: "var(--au-accent)" }} />
                <span>
                  <strong style={{ color: "var(--au-text)" }}>I understand:</strong> GetKhojo plans are <strong>non-refundable</strong>. Free Trial lets me evaluate before paying. I waive refund rights except where Indian law mandates. I accept the <a href="/terms" target="_blank" style={{ color: "var(--au-accent-ink)" }}>Terms</a>, <a href="/privacy" target="_blank" style={{ color: "var(--au-accent-ink)" }}>Privacy</a>, and <a href="/refund" target="_blank" style={{ color: "var(--au-accent-ink)" }}>Refund Policy</a>.
                </span>
              </label>
            </div>
            <button
              className="au-pill au-pill-primary"
              onClick={register}
              disabled={busy}
              style={{ width: "100%" }}>
              {busy ? "CREATING..." : "Create Free Account"} <ArrowRight size={18} />
            </button>

            <div style={{
              textAlign: "center",
              marginTop: 28,
              paddingTop: 22,
              borderTop: "1px solid var(--au-hair)",
              fontSize: 13,
              color: "var(--au-text-2)"
            }}>
              Already have an account?{" "}
              <Link to="/login" style={{
                color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600
              }}>Sign in</Link>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{
              background: "var(--au-glass-strong)",
              border: "1px solid var(--au-border)",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 24,
              fontSize: 13,
              color: "var(--au-text-2)",
              lineHeight: 1.6,
              textAlign: "center"
            }}>
              A verification code has been sent to<br />
              <strong style={{ color: "var(--au-accent-ink)" }}>{email}</strong>
            </div>

            <label style={labelStyle}>
              <KeyRound size={14} /> Verification Code
            </label>
            <input
              className="au-input"
              type="text"
              value={code}
              onChange={e => {
                let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                let parts = raw.match(/.{1,3}/g) || [];
                setCode(parts.join('-'));
              }}
              onKeyDown={e => e.key === "Enter" && !busy && verify()}
              placeholder="XXX-XXX-XXX"
              autoFocus
              style={{
                fontFamily: "var(--au-font-mono)",
                fontSize: 24,
                textAlign: "center",
                letterSpacing: 12,
                fontWeight: 700,
                marginBottom: 24
              }}
            />

            <button
              className="au-pill au-pill-primary"
              onClick={verify}
              disabled={busy}
              style={{ width: "100%", marginBottom: 14 }}>
              {busy ? "VERIFYING..." : "Verify & Continue"} <ArrowRight size={18} />
            </button>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                className="au-pill au-pill-ghost"
                onClick={() => { setStep(1); setCode(""); }}
                style={{ flex: 1 }}>
                <ArrowLeft size={16} /> Change Email
              </button>
              <button
                className="au-pill au-pill-ghost"
                onClick={resend}
                disabled={busy}
                style={{ flex: 1 }}>
                Resend Code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
